/**
 * WHAT TELLS ONE SAVED PLAN FROM ANOTHER, at a glance on Home (owner, 23 Oct 2026: "I could create multiple saved
 * scenarios at different room costs … what's the lever that differentiates the saved models", without filling
 * the cards). One short fact per plan, from what the plan saved:
 *   aged care            the room: "Lakeside Gardens, $700,000 room"
 *   retirement village   the unit: "Banksia Grove, $600,000 entry"
 *   downsizing           the next home: "Elm Street, $850,000 home" / "renting at $650 a week"
 *   granny flat          what was given for the right: "$300,000 given for the right"
 * Null where the plan saved nothing to tell it by (an older record), so the line simply shows the date.
 */
import { formatCurrency } from '../../utils/index.ts';

type Loose = Record<string, unknown> | null | undefined;
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? v as Record<string, unknown> : {});
const num = (v: unknown): number => Number(v) || 0;
const named = (name: unknown, rest: string) => (String(name || '').trim() ? `${String(name).trim()}, ${rest}` : rest);

export function savedPlanLever(record: Loose, typeHint?: string): string | null {
    const r = obj(record);
    // An advice document carries its plan's figures itself, or the plan under `strategy`.
    const inner = obj(r.strategy);
    const type = String(typeHint || r.type || inner.type || '');
    const inputs = { ...obj(inner.inputs), ...obj(r.inputs) };
    const outputs = { ...obj(inner.outputs), ...obj(r.outputs) };
    const card = obj(inputs.card);

    // An aged care advice document keeps the room on its snapshot (buildAgedCareDigitalSOASnapshot's roomCost).
    const snapshotRoom = num(obj(r.soaSnapshot).roomCost);
    if (type === 'aged_care' || num(outputs.roomCost) > 0 || snapshotRoom > 0) {
        const room = num(outputs.roomCost) || num(inputs.radInput) || snapshotRoom;
        if (room <= 0) return null;
        return named(r.homeName ?? inner.homeName, `${formatCurrency(room)} room`);
    }
    if (type === 'retirement_village') {
        const ctx = obj(inputs.planContext);
        const village = obj(card.village);
        const fee = num(ctx.entryFee) || num(village.entryFee) || num(obj(inputs.villageInputs).entryFee);
        if (fee <= 0) return null;
        return named(ctx.villageName ?? village.name, `${formatCurrency(fee)} entry`);
    }
    if (type === 'downsizer') {
        const move = obj(card.move);
        if (move.sellAndRent && num(move.rentPerWeek) > 0) return named(move.homeName, `renting at ${formatCurrency(num(move.rentPerWeek))} a week`);
        if (num(move.newHomePrice) > 0) return named(move.homeName, `${formatCurrency(num(move.newHomePrice))} home`);
        return null;
    }
    if (type === 'granny_flat') {
        const given = num(obj(card.arrangement).given);
        return given > 0 ? `${formatCurrency(given)} given for the right` : null;
    }
    return null;
}
